import { useEffect } from "react";
import supabase from "../api/supabase"; // Import Supabase client
import toast from "react-hot-toast"; // Import toast for notifications
import { DateTime } from "luxon"; // Import Luxon for handling time zones

const AutoReset = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      // Fetch the timeReset and timeZone from the settings table
      const { data: settings, error: settingsError } = await supabase
        .from("settings")
        .select("timeReset, timeZone")
        .single();

      if (settingsError || !settings) {
        console.error("Could not fetch settings:", settingsError);
        return;
      }

      const { timeReset, timeZone } = settings;

      // Define the function inside useEffect to avoid the dependency warning
      const scheduleAutoReset = () => {
        const checkResetTime = () => {
          const now = DateTime.now().setZone(timeZone);
          const resetTime = DateTime.fromISO(timeReset, { zone: timeZone });

          if (now.hour === resetTime.hour && now.minute === resetTime.minute) {
            handleReset(); // Trigger reset when the current time matches the reset time
          }
        };

        // Check every minute
        const intervalId = setInterval(checkResetTime, 60000);

        // Cleanup the interval on component unmount
        return () => clearInterval(intervalId);
      };

      scheduleAutoReset();
    };

    fetchSettings();
  }, []);

  const handleReset = async () => {
    try {
      // Fetch the first row from taskManager to get the earliest date
      const { data: firstTask, error: firstTaskError } = await supabase
        .from("taskManager")
        .select("checkinTime")
        .order("checkinTime", { ascending: true })
        .limit(1)
        .single();

      if (firstTaskError || !firstTask) {
        throw new Error("Could not fetch the first task from taskManager");
      }

      // Extract the date part (YYYY-MM-DD) from checkinTime
      const date = new Date(firstTask.checkinTime).toISOString().split("T")[0];

      // Fetch the last row from taskManager_history to get the most recent date
      const { data: lastHistoryTask, error: lastHistoryTaskError } =
        await supabase
          .from("taskManager_history")
          .select("checkinTime")
          .order("checkinTime", { ascending: false })
          .limit(1)
          .single();

      let date_history;

      if (lastHistoryTaskError && lastHistoryTaskError.code !== "PGRST116") {
        throw new Error(
          "Could not fetch the last task from taskManager_history"
        );
      } else if (lastHistoryTask) {
        date_history = new Date(lastHistoryTask.checkinTime)
          .toISOString()
          .split("T")[0];
      }

      // If the dates are the same, delete rows in taskManager_history with date_history
      if (date_history === date) {
        const { error: deleteError } = await supabase
          .from("taskManager_history")
          .delete()
          .gte("checkinTime", `${date_history}T00:00:00.000Z`)
          .lte("checkinTime", `${date_history}T23:59:59.999Z`);

        if (deleteError) {
          throw new Error(
            "Could not delete old records from taskManager_history"
          );
        }
      }

      // Fetch all tasks from taskManager
      const { data: tasks, error: fetchTasksError } = await supabase
        .from("taskManager")
        .select("*");

      if (fetchTasksError || !tasks) {
        throw new Error("Could not fetch tasks from taskManager");
      }

      // Modify tasks to contain only the date part in checkinTime and remove the 'id' column
      const tasksWithoutId = tasks.map((task) => {
        const { checkinTime, ...rest } = task; // Exclude the 'id' field
        const dateOnly = new Date(checkinTime).toISOString().split("T")[0]; // Extract date only

        return {
          ...rest,
          checkinTime: dateOnly, // Store only the date part
        };
      });

      // Insert tasks without 'id' into taskManager_history
      const { error: insertError } = await supabase
        .from("taskManager_history")
        .insert(tasksWithoutId);

      if (insertError) {
        console.error("Detailed insert error:", insertError);
        throw new Error("Could not insert tasks into taskManager_history");
      }

      // Show success toast notification
      toast.success("Tasks successfully copied to taskManager_history.");
    } catch (error) {
      console.error(error.message);
      toast.error("Error during task reset: " + error.message);
    }
  };

  return null; // This component does not render anything in the UI
};

export default AutoReset;
